import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Brain,
  Activity,
  Zap,
  AlertTriangle,
  Clock,
  Sparkles,
  Loader2,
  CheckCircle2
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsNeuroPage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (pathologyId, pathologyName, content) => {
    setAiLoading({ ...aiLoading, [pathologyId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_neurologie',
          audience: 'adult',
          prompt: `Analyse neurologique pour ${pathologyName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Points clés du diagnostic neurologique
2. Éléments de gravité immédiats
3. Urgences thérapeutiques (délais critiques)
4. Critères d'orientation spécialisée
5. Pièges diagnostiques à éviter`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [pathologyId]: data.response });
        toast.success(`Analyse AI ${pathologyName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [pathologyId]: false });
    }
  };

  const protocols = [
    {
      id: 'avc',
      name: 'AVC - Accident Vasculaire Cérébral',
      icon: Brain,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**ACCIDENT VASCULAIRE CÉRÉBRAL (AVC)**

**DIAGNOSTIC:**
• Déficit neurologique brutal
• Utiliser les scores:
  - **FAST** (Face-Arms-Speech-Time)
  - **NIHSS** (National Institutes of Health Stroke Scale)
  - **Glasgow** pour l'état de conscience

**DÉLAIS CRITIQUES:**
• **Thrombolyse:** < 4h30
• **Thrombectomie:** jusqu'à 24h selon localisation lésion et discussion CHU
• Appel centre spécialisé pour discussion immédiate

**ÉVALUATION INITIALE:**
• Score de Glasgow
• Score NIHSS complet
• Horaire précis de début des symptômes
• Dernier moment vu normal
• Recherche contre-indications thrombolyse

**EXAMENS:**
• Scanner cérébral sans injection en urgence
• Si suspicion HSA: scanner avec injection pour thrombose tronc basilaire
• Glycémie capillaire
• INR si AVK
• Heure dernière prise AOD

**PRISE EN CHARGE GÉNÉRALE:**

**OXYGÉNATION:**
• Objectif SpO2 ≥ 95%
• O2 au masque si nécessaire

**VENTILATION:**
• Normocapnie: EtCO2 30-35 mmHg
• Intubation si:
  - Glasgow < 8
  - Détérioration neurologique
  - Hypoventilation
  - Selon autonomie et projet thérapeutique

**HÉMODYNAMIQUE:**
• VVP avec NaCl 0,9%
• Pas de remplissage systématique

**CONTRÔLE GLYCÉMIQUE:**
• Objectif: 1,4-1,8 g/L (7,7-10 mmol/L)
• Traiter hypoglycémie si < 0,6 g/L (3,3 mmol/L)

**GESTION TENSIONNELLE:**

**AVC ISCHÉMIQUE:**
• **Objectif:** TAS/TAD < 220/120 mmHg
• **Cible 1ère heure:** réduction de 15%
• **Contrôle TA:** toutes les 10 minutes

**SI THROMBOLYSE PRÉVUE:**
• **Objectif:** TAS/TAD < 185/110 mmHg
• **Nicardipine:** 0,5 mg/h, titration par paliers 0,5 mg/h

**AVC HÉMORRAGIQUE:**
• **Objectif:** TAS < 150 mmHg

**HÉMORRAGIE SOUS-ARACHNOÏDIENNE:**
• **Objectif:** TAS < 180 mmHg

**INTUBATION SI NÉCESSAIRE:**
• **Objectif sédation:** Ramsay 6
• Agents selon protocole IOT
• Éviter hypotension lors de l'induction

**OSMOTHÉRAPIE:**
• **Mannitol 20%:** 0,5-1 g/kg (max 500 ml)
• **Indication:** engagement, HTIC

**ORIENTATION:**

**AVC ISCHÉMIQUE:**
• **Discussion immédiate** avec centre neurovasculaire
• Possibilité thrombolyse IV
• Possibilité thrombectomie mécanique
• Transport médicalisé en urgence

**AVC HÉMORRAGIQUE:**
• Si contexte évocateur (céphalée brutale puis coma)
• **Orientation:** neurochirurgie (Rennes ou Brest)
• Transport médicalisé

**SURVEILLANCE:**
• État neurologique (Glasgow, NIHSS)
• Constantes vitales
• Glycémie
• TA toutes les 10 minutes
• Recherche aggravation ou complications

**⚠️ CONTRE-INDICATIONS THROMBOLYSE:**
• Hémorragie intracrânienne
• Chirurgie ou traumatisme récent grave
• AVC récent (< 3 mois)
• Hémorragie digestive récente
• Ponction artérielle récente (site incompressible)
• Troubles de coagulation
• Glycémie < 0,5 g/L ou > 4 g/L
• TAS > 185 ou TAD > 110 mmHg non contrôlée
• Crise comitiale au début
• Amélioration rapide des symptômes`
    },
    {
      id: 'hemorragie-meningee',
      name: 'Hémorragie Méningée',
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**HÉMORRAGIE MÉNINGÉE (HSA) - HÉMORRAGIE SOUS-ARACHNOÏDIENNE**

**DIAGNOSTIC CLINIQUE:**
• **Céphalée brutale** "en coup de tonnerre"
• **Intensité maximale d'emblée**
• Souvent lors d'un effort
• Nausées, vomissements
• Photophobie
• Raideur de nuque (tardive, inconstante)
• Troubles de conscience possibles
• Déficit neurologique focal possible

**SIGNES DE GRAVITÉ:**
• Troubles de conscience
• Coma
• Déficit neurologique
• Convulsions
• Engagement cérébral
• Instabilité hémodynamique

**CLASSIFICATION:**

**FISHER (scanner):**
• Grade 1: Pas de sang visible
• Grade 2: Sang < 1 mm
• Grade 3: Sang > 1 mm (risque vasospasme)
• Grade 4: Sang intraventriculaire ou intraparenchymateux

**HUNT ET HESS (clinique):**
• Grade 1: Asymptomatique ou céphalée minime
• Grade 2: Céphalée modérée à sévère, raideur de nuque
• Grade 3: Confusion, somnolence, déficit focal minime
• Grade 4: Stupeur, hémiparésie modérée à sévère
• Grade 5: Coma, posture de décérébration

**ÉTIOLOGIES:**
• **Anévrisme artériel** (80%)
• Malformation artério-veineuse
• Trouble de coagulation
• Traumatisme (à éliminer)
• Idiopathique

**EXAMENS:**
• **Scanner cérébral sans injection** (en urgence)
  - Sang dans espaces sous-arachnoïdiens
  - Hydrocéphalie
• **Scanner avec injection:**
  - Recherche anévrisme
  - Recherche thrombose tronc basilaire
• **Angioscanner** ou **angio-IRM**
• **Ponction lombaire** si scanner négatif et suspicion forte
• INR si AVK
• Heure dernière prise AOD

**PRISE EN CHARGE:**

**MESURES GÉNÉRALES:**
• Position demi-assise
• Repos strict, calme, obscurité
• Éviter les efforts (Valsalva)
• Scope, SpO2, TA

**OXYGÉNATION:**
• O2 pour SpO2 > 95%

**CONTRÔLE TENSIONNEL:**
• **Objectif:** TAS < 180 mmHg
• **Éviter hypotension** (risque ischémie cérébrale)
• **Nicardipine:** 0,5 mg/h si TAS > 180 mmHg
  - Titration par paliers 0,5 mg/h
• **Surveillance TA:** toutes les 10 minutes

**CONTRÔLE GLYCÉMIQUE:**
• Objectif: 1,4-1,8 g/L
• Traiter hypo et hyperglycémie

**ANALGÉSIE:**
• Céphalées souvent intenses
• **Paracétamol:** 1g IV toutes les 6h
• **Morphine:** titration si douleur non contrôlée
  - 2-3 mg IV toutes les 5 min
• **Éviter:** aspirine, AINS (risque hémorragique)

**ANTIÉMÉTIQUES:**
• **Métoclopramide (Primpéran®):** 10 mg IV
• **OU Ondansétron (Zophren®):** 4-8 mg IV

**SÉDATION SI AGITATION:**
• Aggrave le risque de resaignement
• Benzodiazépines à petite dose

**NEUROPROTECTION:**
• **Nimodipine:** prévention vasospasme
  - Débuter le plus tôt possible
  - 60 mg PO ou SNG toutes les 4h
  - Surveillance TA (risque hypotension)

**CONVULSIONS:**
• **Clonazépam (Rivotril®):** 1 mg IV
• Discuter traitement préventif

**INTUBATION SI:**
• Glasgow ≤ 8
• Détresse respiratoire
• Agitation majeure non contrôlée
• Engagement cérébral
• **Objectif sédation:** Ramsay 6

**OSMOTHÉRAPIE SI HTIC:**
• **Mannitol 20%:** 0,5-1 g/kg IV
• **OU SSH 3%:** 2-5 ml/kg

**COMPLICATIONS:**

**PRÉCOCES:**
• **Resaignement** (20% à J0, 50% à J6)
• **Hydrocéphalie aiguë**
• **HTIC, engagement**
• **Convulsions**
• **Troubles du rythme cardiaque**

**TARDIVES:**
• **Vasospasme** (J4-J14, pic J7)
• **Ischémie cérébrale retardée**
• **Hydrocéphalie chronique**

**ORIENTATION:**
• **Transport médicalisé URGENT**
• **Neurochirurgie (Rennes ou Brest)**
• Appel préalable pour préparer l'accueil
• Chirurgie ou radiologie interventionnelle
• Unité de soins intensifs neurovasculaires

**SURVEILLANCE DURANT TRANSPORT:**
• État neurologique (Glasgow)
• TA, FC, SpO2
• Recherche resaignement (aggravation brutale)
• Éviter toute stimulation excessive`
    },
    {
      id: 'etat-mal-epileptique',
      name: 'État de Mal Épileptique',
      icon: Zap,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**ÉTAT DE MAL ÉPILEPTIQUE (EME)**

**DÉFINITION:**
• Crise convulsive tonico-clonique généralisée durant ≥ 5 minutes
• **OU** ≥ 2 crises sans récupération de conscience entre elles

**EME RÉFRACTAIRE:**
• Persistance de l'EME malgré 2 lignes de traitement

**ÉTIOLOGIES:**

**NEUROLOGIQUES:**
• AVC (ischémique ou hémorragique)
• Traumatisme crânien
• Tumeur cérébrale
• Infection (méningite, encéphalite)
• Épilepsie connue mal équilibrée

**MÉTABOLIQUES:**
• Hypoglycémie
• Hyponatrémie, hypocalcémie
• Hypoxie
• Insuffisance hépatique, rénale

**TOXIQUES:**
• Sevrage alcoolique
• Intoxication médicamenteuse
• Stupéfiants

**AUTRES:**
• Éclampsie
• Fièvre chez l'enfant

**CONDUITE À TENIR:**

**MESURES GÉNÉRALES:**
• Position latérale de sécurité
• Libération des voies aériennes
• Protection du patient (éviter traumatismes)
• Oxygène haute concentration
• Aspiration si sécrétions
• VVP ou IO
• Monitoring (scope, SpO2, TA)
• **Glycémie capillaire** (traiter hypoglycémie)

**1ère LIGNE - BENZODIAZÉPINES (T0):**

**ADULTE:**
• **Clonazépam (Rivotril®):**
  - 0,015 mg/kg IVD (max 1 mg)
  - Soit 1 amp de 1 mg IVL en 30 secondes
  - **Renouveler 1 fois après 5 min** si persistance

• **OU Midazolam:**
  - 0,15 mg/kg IM (dose unique si pas de VVP)
  - OU 0,1 mg/kg IV

• **OU Diazépam (Valium®):**
  - 10 mg IV ou intra-rectal
  - Renouveler 1 fois

**PÉDIATRIQUE:**
• **Clonazépam:**
  - < 30 kg: 0,5 mg IVL
  - > 30 kg: 1 mg IVL

• **OU Diazépam rectal:**
  - < 10 kg: 5 mg
  - 10-15 kg: 7,5 mg
  - > 15 kg: 10 mg

**RÉÉVALUATION après 5 minutes**

**2ème LIGNE (T+5 min) - SI PERSISTANCE:**

**Renouveler Benzodiazépines:**
• Clonazépam 0,015 mg/kg IVD (max 1 mg)

**RÉÉVALUATION après 5 minutes**

**3ème LIGNE (T+10 min) - ANTIÉPILEPTIQUES:**

**FOSPHÉNYTOÏNE (Pro-Dilantin®):**
• **Dose:** 20 mg EP/kg IV
• Perfusion lente: max 3 mg EP/kg/min (max 150 mg/min)
• **Contre-indications:** troubles cardiaques, BAV
• **⚠️ Monitoring cardiaque obligatoire**
• **Effets secondaires:** hypotension, troubles du rythme

**OU PHÉNOBARBITAL (Gardénal®):**
• **Dose:** 15 mg/kg IV
• Perfusion lente: max 1 mg/kg/min (max 100 mg/min)
• **Effets secondaires:** dépression respiratoire, hypotension

**OU LÉVÉTIRACÉTAM (Keppra®):**
• **Dose:** 40-60 mg/kg IV (max 3000 mg)
• Sur 5-10 minutes
• Pas d'effet secondaire hémodynamique majeur
• Alternative intéressante

**RÉÉVALUATION après 10-15 minutes**

**EME RÉFRACTAIRE (T+20-30 min):**

**INTUBATION ET VENTILATION MÉCANIQUE:**

**INDICATIONS:**
• Troubles de conscience persistants
• Crises généralisées persistantes
• Détresse respiratoire
• Épuisement
• Nécessité sédation profonde

**INDUCTION:**
• **Agents de 1ère intention:**
  - **Kétamine:** 1,5-3 mg/kg IV
    > Avantages: peu d'effet hémodynamique
    > Effet anticonvulsivant propre
  - **Thiopental:** 3-5 mg/kg IV
    > Antiépileptique puissant
    > Risque hypotension
  - **Propofol:** 1-2 mg/kg IV
    > Risque hypotension

• **Curare:**
  - Succinylcholine: 1 mg/kg IV
  - OU Rocuronium: 0,6-1,2 mg/kg IV

**SÉDATION CONTINUE:**

**MIDAZOLAM IVSE:**
• Bolus: 0,2 mg/kg
• Entretien: 0,1-0,4 mg/kg/h
• Titration selon activité épileptique

**PROPOFOL IVSE:**
• Bolus: 2 mg/kg
• Entretien: 2-10 mg/kg/h
• **⚠️ Risque:** hypotension, syndrome perfusion propofol

**THIOPENTAL IVSE:**
• Bolus: 3-5 mg/kg
• Entretien: 3-5 mg/kg/h
• Antiépileptique puissant
• Nécessite monitorage hémodynamique

**OBJECTIF:** Ramsay 6 (coma profond)

**RECHERCHE ÉTIOLOGIQUE:**

**BILAN SANGUIN:**
• Ionogramme (Na, Ca, Mg)
• Glycémie
• Fonction rénale, hépatique
• NFS
• Recherche toxiques si contexte

**IMAGERIE:**
• Scanner cérébral en urgence
• IRM si disponible

**PONCTION LOMBAIRE:**
• Si suspicion méningite/encéphalite
• Après scanner (éliminer HTIC)

**EEG:**
• Dès que possible
• Monitoring continu si disponible

**SURVEILLANCE:**
• État neurologique (Glasgow)
• Scope, SpO2, TA, FC
• Capnographie si intubé
• Température
• Glycémie
• Durée et nombre de crises
• Réponse au traitement

**TRAITEMENT ÉTIOLOGIQUE:**
• Corriger hypoglycémie: G30% 2 amp IV
• Corriger hyponatrémie: SSH si sévère
• Antibiothérapie si méningite
• Antidote si intoxication

**ORIENTATION:**
• **Réanimation** si intubation/sédation
• **Neurologie/USI** si contrôle sans intubation
• Discussion avec neurologue
• Transport médicalisé
• EEG à prévoir en urgence

**⚠️ PIÈGES:**
• Ne pas retarder benzodiazépines
• Doser correctement les antiépileptiques
• Rechercher et traiter hypoglycémie
• Ne pas oublier recherche étiologique
• Monitoring cardiaque avec fosphénytoïne`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-xl border border-purple-400/30">
            <Brain className="w-8 h-8 text-purple-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">Référentiels Neurologie</h1>
            <p className="text-[hsl(var(--text-dim))]">Urgences neurologiques - Protocoles SMUR 2025</p>
          </div>
        </div>
        
        <Alert className="bg-purple-500/10 border-purple-400/30">
          <Clock className="w-4 h-4 text-purple-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <strong>Urgences temps-dépendantes :</strong> Délais critiques pour thrombolyse (4h30) et thrombectomie (24h)
          </AlertDescription>
        </Alert>
      </div>

      {/* Main Content */}
      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-[hsl(var(--text))]">
            <Activity className="w-5 h-5 text-purple-400" />
            Protocoles Neurologiques
          </CardTitle>
          <CardDescription>
            Sélectionnez un protocole pour afficher les recommandations détaillées
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] pr-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {protocols.map((protocol) => {
                const Icon = protocol.icon;
                const isLoading = aiLoading[protocol.id];
                const aiResult = aiResults[protocol.id];

                return (
                  <AccordionItem
                    key={protocol.id}
                    value={protocol.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                  >
                    <AccordionTrigger
                      className="px-6 py-4 hover:no-underline hover:bg-[hsl(var(--bg-elev))] transition-colors"
                      data-testid={`accordion-trigger-${protocol.id}`}
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className={`p-2 ${protocol.bg} rounded-lg border ${protocol.border}`}>
                          <Icon className={`w-5 h-5 ${protocol.color}`} />
                        </div>
                        <span className="font-semibold text-[hsl(var(--text))] text-left">
                          {protocol.name}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 py-4 space-y-4">
                      <div className="space-y-4">
                        {/* Contenu médical */}
                        <div className="prose prose-invert max-w-none">
                          <div className="text-sm text-[hsl(var(--text))] whitespace-pre-wrap leading-relaxed">
                            {protocol.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return (
                                  <h3 key={idx} className="text-lg font-semibold text-cyan-400 mt-4 mb-2">
                                    {line.replace(/\*\*/g, '')}
                                  </h3>
                                );
                              }
                              if (line.startsWith('• ') || line.startsWith('- ')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 my-1">
                                    <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              if (line.startsWith('⚠️')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              return <p key={idx} className="my-1">{line}</p>;
                            })}
                          </div>
                        </div>

                        {/* Bouton Analyse AI */}
                        <Button
                          onClick={() => handleAiAnalysis(protocol.id, protocol.name, protocol.content)}
                          disabled={isLoading}
                          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                          data-testid={`ai-button-${protocol.id}`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyse AI en cours...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Analyse AI de ce protocole
                            </>
                          )}
                        </Button>

                        {/* Résultat AI */}
                        {aiResult && (
                          <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                            <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                              <Sparkles className="w-5 h-5" />
                              Analyse AI
                            </h3>
                            <div className="prose prose-invert max-w-none">
                              <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${protocol.id}`}>
                                {aiResult}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
